import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import { AssessmentDetails } from '@/__generated__/graphql';
import { joinStringBy, titleCaseSentence } from '@/utils';
import { NOT_APPLICABLE_LABEL } from '@/constants';

export const useGetGenericDetailsPanels = (assemblyDetails: Record<string, string> | undefined) => {
  const assemblyTemplate = useAtomValue(state.assemblyTemplate);

  const detailsData = useMemo(() => {
    if (!assemblyDetails || !assemblyTemplate?.assessmentDetails) return;

    const grouped: Record<string, AssessmentDetails[]> = {};
    for (const detail of assemblyTemplate.assessmentDetails) {
      if (!detail.isVisible) continue;

      if (!grouped[detail.type]) grouped[detail.type] = [];
      grouped[detail.type].push(detail);
    }
    const result = Object.entries(grouped).map(([type, groupDetails]) => {
      groupDetails.sort((a, b) => a.position - b.position);

      const data: [string, string | number, string][] = [];

      for (const detail of groupDetails) {
        for (const option of detail.options || []) {
          const key = option.fieldKey;
          const field = assemblyTemplate?.fields.find((field) => field.key === key);
          if (field) {
            const value = assemblyDetails[key] ?? NOT_APPLICABLE_LABEL;
            const fieldName: string = field.name ? joinStringBy(field.name, ' ', '_') : '';
            data.push([field.key ?? '', value, fieldName]);
          }
        }
      }

      return {
        title: titleCaseSentence(type.replace(/_/g, ' ')),
        data,
      };
    });

    return result;
  }, [assemblyTemplate?.assessmentDetails, assemblyTemplate?.fields, assemblyDetails]);

  const displayableFields = useMemo(() => {
    return (
      assemblyTemplate?.fields
        .map((field) => field.key)
        .filter((key): key is string => typeof key === 'string') ?? []
    );
  }, [assemblyTemplate]);

  return {
    detailsData,
    displayableFields,
  };
};
